import React from 'react';

import { CalendarDays } from 'lucide-react';

import type { BillingDatePreset, BillingDateRange } from '../utils/billingUsageFormat';
import {
  BILLING_DATE_PRESETS,
  createBillingDateRange,
  getBillingDatePresetLabel,
} from '../utils/billingUsageFormat';

interface BillingDateRangeFilterProps {
  value: BillingDateRange;
  onChange: (value: BillingDateRange) => void;
  disabled?: boolean;
}

const normalizeCustomRange = (
  current: BillingDateRange,
  field: 'startDate' | 'endDate',
  value: string,
): BillingDateRange => {
  const next = {
    ...current,
    preset: 'custom' as const,
    [field]: value,
  };
  if (next.startDate && next.endDate && next.startDate > next.endDate) {
    return field === 'startDate'
      ? { ...next, endDate: next.startDate }
      : { ...next, startDate: next.endDate };
  }
  return next;
};

const BillingDateRangeFilter: React.FC<BillingDateRangeFilterProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const handlePresetClick = (preset: BillingDatePreset) => {
    if (preset === 'custom') {
      onChange({ ...value, preset: 'custom' });
      return;
    }
    onChange(createBillingDateRange(preset));
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-dark-border bg-dark-card px-3 py-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-center gap-2 text-xs text-dark-text-secondary">
        <CalendarDays className="h-4 w-4 text-theme-color" />
        时间范围
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="inline-flex rounded-lg border border-dark-border bg-dark-bg p-1">
          {BILLING_DATE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              disabled={disabled}
              onClick={() => handlePresetClick(preset)}
              className={`h-8 min-w-[62px] rounded-md px-3 text-xs transition-colors ${
                value.preset === preset
                  ? 'bg-theme-color text-white'
                  : 'text-dark-text-secondary hover:text-dark-text'
              } disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {getBillingDatePresetLabel(preset)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={value.startDate}
            disabled={disabled || value.preset === 'all'}
            onChange={(event) =>
              onChange(normalizeCustomRange(value, 'startDate', event.target.value))
            }
            className="h-9 w-[136px] rounded-lg border border-dark-border bg-dark-bg px-3 text-xs text-dark-text outline-none transition-colors focus:border-theme-color disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-xs text-dark-text-secondary">至</span>
          <input
            type="date"
            value={value.endDate}
            disabled={disabled || value.preset === 'all'}
            onChange={(event) =>
              onChange(normalizeCustomRange(value, 'endDate', event.target.value))
            }
            className="h-9 w-[136px] rounded-lg border border-dark-border bg-dark-bg px-3 text-xs text-dark-text outline-none transition-colors focus:border-theme-color disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>
    </div>
  );
};

export default BillingDateRangeFilter;
